import { usePlaylistActions } from 'app/_hooks/usePlaylistActions'
import { useLoginCheck } from 'app/_hooks/useLoginCheck'
import throttle from 'lodash.throttle'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import * as GA from '~/libs/ga'

const eventThresholds = [
  { currentTime: 1, action: 'video_views_time_watched_second' },
  { currentTime: 3, action: 'video_views_3s_watched_count' },
  { currentTime: 5, action: 'video_views_5s_watched_count' },
  { currentTime: 10, action: 'video_views_10s_watched_count' },
  { currentTime: 15, action: 'video_views_15s_watched_count' },
  { currentTime: 30, action: 'video_views_30s_watched_count' },
]

export interface VideoPreviewModalActions {
  onClose: () => void
  onSetup: (video: HTMLVideoElement) => void
  onClickVideoPlay: (videoId: number) => void
  onClickAddPlaylist: (videoId: number) => void
  sendTrailInsightsEvent: (action: string, value?: string) => void
}

type Props = {
  videoId: string
  onClose: () => void
}

export const useVideoPreviewModalActions = ({
  videoId,
  onClose: handleClose,
}: Props): VideoPreviewModalActions => {
  const { t } = useTranslation()
  const { push } = useRouter()
  const asPath = usePathname()
  const {
    setPlayListVideoActionUiType: setUiType,
    setTargetActionPlayListVideoInfo: setPlayListVideoInfo,
  } = usePlaylistActions()
  const checkLogin = useLoginCheck()
  const timeUpdateEvents = useRef<Set<number>>(new Set())
  const didSendWatchComplete = useRef(false)
  const screen = useMemo(() => {
    if (asPath === '/') {
      return 'stream'
    } else if (asPath?.startsWith('/search/categories')) {
      return 'category_videos'
    } else if (asPath?.startsWith('/search/result')) {
      return 'search_result'
    }
  }, [asPath])

  const sendTrailInsightsEvent = useCallback(
    (action: string, value?: string) => {
      if (screen === undefined) return
      GA.sendEvent({
        event: 'trail_insights',
        screen,
        category: 'trail',
        action,
        label: `${videoId}`,
        value,
      })
    },
    [screen, videoId],
  )

  const onVolumeChange = useCallback(
    () =>
      throttle(() => {
        sendTrailInsightsEvent('video_views_volume_change')
      }, 3000),
    [sendTrailInsightsEvent],
  )

  const onTimeUpdate = useCallback(
    (video: HTMLVideoElement) => {
      eventThresholds.forEach(({ currentTime, action }) => {
        if (video.currentTime >= currentTime && !timeUpdateEvents.current.has(currentTime)) {
          sendTrailInsightsEvent(action)
          timeUpdateEvents.current.add(currentTime)
        }
      })
      if (!didSendWatchComplete.current && video.currentTime / video.duration > 0.9) {
        sendTrailInsightsEvent('video_views_complete_watched_count')
        didSendWatchComplete.current = true
      }
    },
    [sendTrailInsightsEvent],
  )

  const onSetup = useCallback(
    (video: HTMLVideoElement) => {
      video.addEventListener('play', () => sendTrailInsightsEvent('video_views_play_count'))
      video.addEventListener('pause', () => sendTrailInsightsEvent('video_views_stop_count'))
      video.addEventListener('volumechange', onVolumeChange)
      video.addEventListener('enterpictureinpicture', () =>
        sendTrailInsightsEvent('video_views_pip_on'),
      )
      video.addEventListener('leavepictureinpicture', () =>
        sendTrailInsightsEvent('video_views_pip_off'),
      )
      video.addEventListener('ratechange', () => {
        sendTrailInsightsEvent('video_views_speed_value', `${video.playbackRate}`)
      })
      video.addEventListener(
        'timeupdate',
        throttle(() => onTimeUpdate(video), 1000),
      )
      // video.addEventListener('ended', () => {})
    },
    [onVolumeChange, sendTrailInsightsEvent, onTimeUpdate],
  )

  const sendGaInfo = useCallback(
    (action: string, category: string) => {
      if (screen === undefined) return
      GA.sendEvent({
        event: 'click',
        screen,
        category,
        action,
        label: `${videoId}`,
      })
    },
    [screen, videoId],
  )

  const onClose = useCallback(() => {
    handleClose()
    sendGaInfo('traill_close', 'arena')
  }, [sendGaInfo, handleClose])

  const onClickVideoPlay = useCallback(
    (videoId: number) => {
      handleClose()
      sendGaInfo('traill_play_button', 'button')
      void push(`/videos/${videoId}`)
    },
    [push, sendGaInfo, handleClose],
  )

  const onClickAddPlaylist = useCallback(
    (videoId: number) => {
      sendGaInfo('traill_mylist', 'icon')
      if (!checkLogin({
        notSignedIn: t('please_login_to_add'),
        noProfile: t('please_create_profile_to_add'),
        conflict: t('require_another_login'),
        onRedirect: handleClose,
      })) {
        return
      }
      setUiType('select')
      setPlayListVideoInfo({ videoId })
    },
    [checkLogin, t, sendGaInfo, setPlayListVideoInfo, setUiType, handleClose],
  )

  return {
    onClose,
    onSetup,
    onClickVideoPlay,
    onClickAddPlaylist,
    sendTrailInsightsEvent,
  }
}
